import { TRefElementInstance } from 'widgets/toolbox/RefElement';
import { TButtonInstance } from 'widgets/global/Button';
import AlternativeImageClassCreator from 'widgets/product/AlternativeImage';
import { isSmallView, isTouchDevice } from 'widgets/toolbox/viewtype';
import { TCoreCarouselInstance } from 'core/global/CoreCarousel';

type TSwatchImageData = {
    url?: string;
    color?: string;
    productUrl?: string;
    altImage?: string;
    altImageTitle?: string;
    altImageSrcsetLg?: string;
    altImageMediaLg?: string;
    altImageSrcsetMd?: string;
    altImageMediaMd?: string;
    altImageSrcsetSm?: string;
    altImageMediaSm?: string;
    variationGroupId?: string;
    value?: string;
};

/**
 * @description CoreProductTile implementation
 * @param ProductTile Base widget for extending
 * @returns CoreProductTile widget
 */
function CoreProductTileClassCreator(ProductTile: import('widgets/product/ProductTile').TProductTile) {
    const WithAlternativeImage = AlternativeImageClassCreator(ProductTile);

    /**
     * @category widgets
     * @subcategory product
     * @class CoreProductTile
     * @augments ProductTile
     * @classdesc Core ProductTile Widget provide with extended methods
     */
    class CoreProductTile extends WithAlternativeImage {
        swatchImageActive = false;

        selectedValue: string | undefined;

        decision = '';

        isCarouselLayoutActive = false;

        /**
         * @description Returns widget preferences, including default values.
         * @returns Merged preferences object
         */
        prefs() {
            return {
                heroTile: '',
                carouselView: false,
                productName: '',
                optimizelyFeatureFlagKey: '',
                ...super.prefs()
            };
        }

        /**
         * @description Initializes the widget and inserts the alternative image if the `heroTile` preference is set.
         */
        init() {
            super.init();

            if (this.prefs().heroTile) {
                this.insertAlternativeImage();
            }

            if (this.prefs().optimizelyFeatureFlagKey) {
                this.initOptimizely();
            } else {
                this.applySwipeLayout();
            }

            this.eventBus().on('viewtype.change', 'applySwipeLayout');
            this.eventBus().on('optimizely.viewtype.change', 'initOptimizely');
        }

        /**
         * @description Executes when user clicks on product details link on product tile.
         * Usually used by analytics etc.
         * @emits tile.product.link.click
         * @param link - product link
         */
        onProductLinkClick(link: TRefElementInstance) {
            this.eventBus().emit('tile.product.link.click', link, {
                carouselView: this.prefs().carouselView
            });
        }

        /**
         * @description Extracts swatch image data from a given link element.
         *
         * @param link - The swatch DOM element wrapped in a TRefElementInstance
         * @returns Swatch image data including URL, color, product URL, alt image, and alt image title
         */
        getSwatchImage(link: TRefElementInstance): TSwatchImageData {
            return {
                url: link.data<string>('productImageUrl'),
                color: link.data<string>('displayValue'),
                productUrl: link.attr('href') || link.data<string>('url'),
                altImage: link.data<string>('productAltImageUrl'),
                altImageTitle: link.data<string>('productAltImageTitle'),
                altImageSrcsetLg: link.data<string>('productAltImageSrcsetLg'),
                altImageMediaLg: link.data<string>('productAltImageMediaLg'),
                altImageSrcsetMd: link.data<string>('productAltImageSrcsetMd'),
                altImageMediaMd: link.data<string>('productAltImageMediaMd'),
                altImageSrcsetSm: link.data<string>('productAltImageSrcsetSm'),
                altImageMediaSm: link.data<string>('productAltImageMediaSm'),
                variationGroupId: link.data<string>('variationGroupId'),
                value: link.attr('data-attr-value') || link.data<string>('attrValue')
            };
        }

        /**
         * @description Sets the visible color title on the product tile.
         * @param  swatchColor - The color name to display
         * @param  [setDefault=false] - Whether to store this color as the default
         */
        setColorTitle(swatchColor: string, setDefault = false) {
            if (!swatchColor) {
                return;
            }

            const colorTitle = this.ref('productColor');

            colorTitle?.setText(swatchColor);

            if (setDefault) {
                colorTitle?.data('colorTitle', swatchColor);
            }
        }

        /**
         * @description Resets the product color title to the previously stored default.
         */
        resetColorTitle() {
            const colorTitle = this.ref('productColor');

            colorTitle?.setText(colorTitle.data<string>('colorTitle') ?? '');
        }

        /**
         * @description Updates the product URL on the tile's image link.
         * @param  productUrl - New product URL
         */
        setProductUrl(productUrl: string) {
            if (!productUrl) {
                return;
            }

            this.ref('tileNameLink')?.attr('href', productUrl);
            this.ref('tileImageLink')?.attr('href', productUrl);
            this.ref('tileCarouselLink')?.attr('href', productUrl);
        }

        /**
         * @description Sets the slider from swatch.
         * @param imageData - The image data
         */
        setSliderFromSwatch(imageData: TSwatchImageData) {
            const variationGroupId = imageData.variationGroupId;
            const tileImageLink = this.ref<HTMLAnchorElement>('tileCarouselLink').get(0);
            const variationGroup = tileImageLink?.querySelector(`[data-variation-group-id="${variationGroupId}"]`);

            const sliderTrack = tileImageLink?.querySelector('.b-product_slider-track');

            if (sliderTrack && variationGroup) {
                sliderTrack.innerHTML = variationGroup.innerHTML;

                if (sliderTrack.firstElementChild instanceof HTMLElement) {
                    sliderTrack.scrollLeft = 0;
                }

                this.getById('mobileCarousel', (mobileCarousel: TCoreCarouselInstance) => {
                    mobileCarousel?.loadImages();
                    mobileCarousel?.update();
                });
            }
        }

        /**
         * @description Sets the image displayed on the product tile based on swatch data.
         *
         * @param imageData - Object containing swatch image details (URL, color, alt text, etc.)
         * @param [options] - Additional options
         * @param [options.replaceMainImage=false] - Whether to fully replace the main image with the swatch image
         */
        setImageFromSwatch(imageData: TSwatchImageData, { replaceMainImage = false }: { replaceMainImage?: boolean } = {}) {
            if (!imageData) {
                return;
            }

            if (this.isCarouselLayoutActive) {
                this.setSliderFromSwatch(imageData);
                this.swatchImageActive = true;
                this.displayPriceFor(imageData.value);

                return;
            }

            if (replaceMainImage && imageData?.url) {
                this.ref('tileImage')
                    .get(0)
                    ?.parentElement?.querySelectorAll('source')
                    .forEach(source => {
                        source.remove();
                    });
                this.ref('tileImage').get(0)?.setAttribute('src', imageData.url);
                this.ref('tileImageSwatch').empty();
                this.ref('alternativeViewContainer')
                    .data('alt-image-url', imageData?.altImage ?? '')
                    .data('alt-image-title', imageData?.altImageTitle ?? '')
                    .data('alt-image-srcset-lg', imageData?.altImageSrcsetLg ?? '')
                    .data('alt-image-media-lg', imageData?.altImageMediaLg ?? '')
                    .data('alt-image-srcset-md', imageData?.altImageSrcsetMd ?? '')
                    .data('alt-image-media-md', imageData?.altImageMediaMd ?? '')
                    .data('alt-image-srcset-sm', imageData?.altImageSrcsetSm ?? '')
                    .data('alt-image-media-sm', imageData?.altImageMediaSm ?? '');
                this.ref('alternativeViewPicture').empty();

                this.imageInserted = false;

                this.displayPriceFor(imageData.value);

                return;
            }

            const swatchImageElement = document.createElement('img');

            if (imageData?.url) {
                swatchImageElement.src = imageData?.url;
            }

            if (imageData?.color) {
                swatchImageElement.alt = imageData?.color;
            }

            this.ref('tileImageSwatch').get(0)?.replaceChildren(swatchImageElement);

            this.swatchImageActive = true;

            this.displayPriceFor(imageData.value);
        }

        /**
         * @description Sets the visibility state of price elements.
         * Shows the price for the given value, or default price if not found.
         * Other prices are made inert, which keeps them in the layout but invisible (see b-product_tile-price),
         * so the tile height doesn't change on swatch hover.
         * @param value - The value of the selected swatch
         * @returns The visible price element
         */
        private setPriceVisibility(value: string | undefined): Element | null {
            const priceElements = Array.from(this.ref('self').get()?.querySelectorAll<HTMLElement>('[data-price-for]') ?? []);
            const visiblePriceElement =
                priceElements.find(priceElement => priceElement.dataset.priceFor === value) ||
                priceElements.find(priceElement => priceElement.dataset.priceFor === 'default') ||
                null;

            priceElements.forEach(priceElement => {
                priceElement.toggleAttribute('inert', priceElement !== visiblePriceElement);
            });

            return visiblePriceElement;
        }

        /**
         * @description Updates the 30-day best price display based on swatch value.
         * @param value - The value of the selected swatch
         * @param visiblePriceValue - The current visible price value
         */
        private update30DaysBestPrice(value: string | undefined, visiblePriceValue: number): void {
            const product30daysBestPrice = this.ref('product30daysBestPrice');

            if (!product30daysBestPrice) {
                return;
            }

            const swatchesPricesObject = product30daysBestPrice.data('swatchesPrices') ?? {};
            let swatchPrice = '';
            let swatchPriceVal = 0;

            if (value) {
                swatchPriceVal = swatchesPricesObject[value]?.value ?? 0;
                swatchPrice = swatchesPricesObject[value]?.message ?? '';

                if (swatchPriceVal > 0 && swatchPriceVal === visiblePriceValue) {
                    swatchPrice = '';
                }
            } else {
                swatchPrice = product30daysBestPrice.data('defaultPrice') ?? '';
            }

            product30daysBestPrice.setText(swatchPrice);
        }

        /**
         * @description Displays the price element corresponding to the selected swatch value.
         * Hides all other price elements.
         * @param value - The value of the selected swatch
         */
        displayPriceFor(value: string | undefined) {
            const visiblePriceElement = this.setPriceVisibility(value);
            const currentPriceValue = Number((visiblePriceElement as HTMLElement)?.getAttribute('data-price')) || 0;

            this.update30DaysBestPrice(value, currentPriceValue);
        }

        displayBadgesFor(colorId: string) {
            if (!colorId) {
                return;
            }

            const allBadges = this.ref('self').get(0)?.querySelectorAll('.js-badges');

            if (allBadges && allBadges.length > 0) {
                allBadges.forEach(badge => {
                    (badge as HTMLElement).style.display = 'none';
                });
            }

            const badgesSelector = colorId === 'default' ? '.js-badges-swatch-default' : `.js-badges-swatch-${colorId}`;

            const badges = this.ref('self').get(0)?.querySelectorAll(badgesSelector);

            if (badges && badges.length > 0) {
                (badges[0] as HTMLElement).style.display = 'flex';
            }
        }

        /**
         * @description Toggles visibility between main and swatch images.
         * @param isMainImage - Whether to activate the main image
         */
        activateImage(isMainImage: boolean) {
            this.ref('tileImageSwatch').toggleClass('m-alt', isMainImage);
            this.ref('tileImage').get(0)?.parentElement?.classList.toggle('m-alt', !isMainImage);
        }

        /**
         * @description Handles hover interaction over a swatch, displaying the corresponding image and color.
         *
         * @param link - product link
         */
        onSwatchHover(link: TRefElementInstance) {
            // On touch devices the emulated "mouseenter" fires within a tap, before "click".
            // Mutating prices/badges here shifts the layout and the tap's click gets lost.
            if (isTouchDevice() || this.swatchImageActive || link.hasAttr('data-selected')) {
                return;
            }

            const imageData = this.getSwatchImage(link);

            this.setImageFromSwatch(imageData);

            this.displayBadgesFor(imageData.value ?? '');

            this.activateImage(false);
            this.setColorTitle(imageData.color ?? '');
        }

        /**
         * @description Handles removal of hover focus from a swatch, restoring the main image and color.
         */
        onSwatchUnfocus() {
            if (isTouchDevice()) {
                return;
            }

            if (this.swatchImageActive) {
                const selectedSwatch = this.ref('swatchesContainer').get(0)?.querySelector<HTMLElement>('.m-selected');
                const selectedVariationGroupId =
                    selectedSwatch?.getAttribute('data-variation-group-id') ||
                    (typeof this.config.variationGroupId === 'string' ? this.config.variationGroupId : undefined);

                if (this.isCarouselLayoutActive && selectedVariationGroupId) {
                    this.setSliderFromSwatch({
                        variationGroupId: selectedVariationGroupId
                    });
                }

                this.activateImage(true);
                this.resetColorTitle();
                this.swatchImageActive = false;
                this.displayPriceFor(this.selectedValue);
                this.displayBadgesFor(this.selectedValue ?? 'default');
            }
        }

        /**
         * @description Handles click on a swatch. Updates the image, product URL, and selection state.
         * @param link - The swatch DOM element wrapped in a TRefElementInstance
         */
        onSwatchClick(link: TRefElementInstance) {
            if (!link.data('selected')) {
                const imageData = this.getSwatchImage(link);

                this.selectedValue = imageData.value;
                const selectedSwatch = this.ref('swatchesContainer').get(0)?.querySelector('.m-selected');
                const linkElement = link.get();

                this.config.variationGroupId = imageData.variationGroupId;
                this.setImageFromSwatch(imageData, {
                    replaceMainImage: true
                });
                this.ref('alternativeViewContainer').toggleClass('b-product_tile_alt_view', !!imageData.altImage);
                this.setProductUrl(imageData.productUrl ?? '');
                this.setColorTitle(imageData.color ?? '', true);
                this.activateImage(true);
                this.swatchImageActive = false;
                selectedSwatch?.removeAttribute('data-selected');
                selectedSwatch?.classList.remove('m-selected');
                link.data('selected', true).addClass('m-selected');

                // Update accessibility attributes for all swatches
                const allSwatches = this.ref('swatchesContainer').get(0)?.querySelectorAll('a');

                this.displayBadgesFor(imageData.value ?? 'default');

                allSwatches?.forEach(swatchLink => {
                    const isSelected = swatchLink === linkElement;

                    swatchLink.setAttribute('tabindex', isSelected ? '0' : '-1');

                    if (isSelected) {
                        swatchLink.focus();
                    }
                });

                this.eventBus().emit('product.tile.swatch.select', link);
            } else {
                window.location.href = link.data('selected', true).data('url') ?? '';
            }
        }

        /**
         * @description On modal window show callback
         * @emits "product.tile.qv.open"
         */
        onModalShow() {
            this.eventBus().emit('product.tile.qv.open', {
                gtmInfo: this.data('analytics')
            });
        }

        showQuickView(button: TButtonInstance) {
            /**
             * @description Event dispatched, when Global Modal should be triggered
             * @event module:events#dialogshow
             */
            this.eventBus().emit(
                'dialogshow',
                {
                    url: button.data('url'),
                    wrapperClasses: 'm-quick_view',
                    ariaLabel: `${button.getText().trim()} ${this.prefs().productName}`,
                    attributes: {
                        'data-tau-unique': 'quick_view_dialog'
                    }
                },
                this.onModalShow.bind(this)
            );
        }

        async initOptimizely() {
            if (!this.prefs().optimizelyFeatureFlagKey) {
                return;
            }

            this.decision = (await this.callIfExists('decideFeatureFlagVariation', this.prefs().optimizelyFeatureFlagKey)) as string;
            this.applySwipeLayout();
        }

        applySwipeLayout() {
            const carousel = this.ref('imageCarousel')?.get();
            const staticImg = this.ref('staticImage')?.get();

            if (!carousel || !staticImg) {
                return;
            }

            const showCarousel = isSmallView() && (this.decision === 'treatment_1' || !this.prefs().optimizelyFeatureFlagKey);

            this.isCarouselLayoutActive = showCarousel;
            carousel.toggleAttribute('hidden', !showCarousel);
            staticImg.toggleAttribute('hidden', showCarousel);

            // Load deferred carousel images only when the carousel is actually shown
            if (showCarousel) {
                this.getById('mobileCarousel', (mobileCarousel: TCoreCarouselInstance) => {
                    mobileCarousel?.loadImages();

                    if (this.decision === 'treatment_1') {
                        mobileCarousel?.refs?.self?.get()?.querySelector('.b-product_slider-pagination')?.classList.add('m-pagination_dots');
                    }
                });
            }
        }
    }

    return CoreProductTile;
}

export type TCoreProductTile = ReturnType<typeof CoreProductTileClassCreator>;

export type TCoreProductTileInstance = InstanceType<TCoreProductTile>;

export default CoreProductTileClassCreator;
